import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { CheckCircle2, Loader2, PauseCircle, PlayCircle, XOctagon, Users, Sparkles } from "lucide-react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Badge } from "../ui/badge";
import { adminApi, formatApiErrorDetail } from "../../lib/authApi";
import { OrderStatusBadge, StageTrack, OrderParts, ProgressList, OrderTimeline, fmtDate, fmtINR, QUALITY_STATES, DELIVERY_STATES } from "./OrderShared";
import { QualitySummary, DeliverySummary } from "./QualityDeliverySummary";
import { QualityPanel } from "./QualityPanel";
import { DispatchPanel } from "./DispatchPanel";
import { OrderDocuments } from "./OrderDocuments";

const Candidates = ({ order, onAssigned }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [assigning, setAssigning] = useState(null);
  const [note, setNote] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const res = await adminApi.get(`/admin/orders/${order.id}/candidates`);
      setData(res.data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  const assign = async (c) => {
    setAssigning(c.partner_id);
    try {
      const res = await adminApi.post(`/admin/orders/${order.id}/assign`, { partner_id: c.partner_id, note });
      toast.success(`Assigned to ${c.company_name}.`);
      onAssigned(res.data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setAssigning(null);
    }
  };

  if (!data) {
    return (
      <Button size="sm" variant="outline" disabled={loading} onClick={load} className="rounded-full" data-testid={`admin-order-candidates-btn-${order.id}`}>
        {loading ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" strokeWidth={1.5} /> : <Users className="h-4 w-4 mr-1.5" strokeWidth={1.5} />} Find partners
      </Button>
    );
  }
  return (
    <div className="w-full mt-2 p-5 rounded-lg bg-muted/40 border border-border space-y-3" data-testid={`admin-order-candidates-${order.id}`}>
      <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
        <Sparkles className="h-3.5 w-3.5 text-primary" strokeWidth={1.5} />
        Deterministic ranking ({data.method}) · required: {data.required_processes.join(", ")} · {data.approved_partner_count} approved partner(s)
      </div>
      <Input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Assignment note for the partner (optional)" data-testid={`admin-order-assign-note-${order.id}`} />
      {data.candidates.length === 0 && <p className="text-sm text-muted-foreground" data-testid={`admin-order-no-candidates-${order.id}`}>No approved partner offers the required process(es). Onboard or approve a partner with matching capabilities.</p>}
      {data.candidates.map((c, i) => (
        <div key={c.partner_id} className="p-4 rounded-lg bg-card border border-border" data-testid={`admin-order-candidate-${order.id}-${c.partner_id}`}>
          <div className="flex flex-wrap items-center gap-3">
            <span className="font-mono-tech text-xs text-muted-foreground">#{i + 1}</span>
            <span className="font-semibold font-heading">{c.company_name}</span>
            <Badge variant="secondary">Score {c.score}</Badge>
            <Badge variant="outline">Coverage {c.coverage}</Badge>
            <Badge variant="outline">{c.machine_count} machine(s)</Badge>
            {c.previously_declined && <Badge variant="outline" className="text-destructive border-destructive/40">Declined earlier</Badge>}
            {(c.city || c.state) && <span className="text-xs text-muted-foreground">{[c.city, c.state].filter(Boolean).join(", ")}</span>}
            <Button size="sm" disabled={!!assigning} onClick={() => assign(c)} className="ml-auto rounded-full" data-testid={`admin-order-assign-btn-${order.id}-${c.partner_id}`}>
              {assigning === c.partner_id ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" strokeWidth={1.5} /> : <CheckCircle2 className="h-4 w-4 mr-1.5" strokeWidth={1.5} />} Assign
            </Button>
          </div>
          <ul className="mt-2 text-xs text-muted-foreground list-disc pl-5 space-y-0.5">
            {c.reasons.map((r, j) => <li key={j}>{r}</li>)}
          </ul>
        </div>
      ))}
    </div>
  );
};

export const AdminOrdersSection = ({ orders, setOrders, onDownloadFile, downloading }) => {
  const [acting, setActing] = useState(null);
  const [notes, setNotes] = useState({});
  const [params] = useSearchParams();
  const focus = params.get("order");
  useEffect(() => {
    const target = focus && orders.find((x) => x.order_number === focus);
    if (target) document.querySelector(`[data-testid="admin-order-card-${target.id}"]`)?.scrollIntoView({ behavior: "smooth", block: "start" });
  }, [focus, orders]);

  const upsert = (o) => setOrders((prev) => prev.map((x) => (x.id === o.id ? o : x)));

  const act = async (o, fn, ok) => {
    setActing(o.id);
    try {
      const { data } = await fn();
      upsert(data);
      toast.success(ok);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setActing(null);
    }
  };
  const note = (o) => notes[o.id] || "";
  const confirm = (o) => act(o, () => adminApi.post(`/admin/orders/${o.id}/confirm`, { note: note(o) }), "Order confirmed.");
  const setStatus = (o, status) => act(o, () => adminApi.patch(`/admin/orders/${o.id}/status`, { status, note: note(o) }), `Order ${status.replace(/_/g, " ").toLowerCase()}.`);

  if (orders.length === 0) {
    return <p className="text-sm text-muted-foreground py-8" data-testid="admin-orders-empty">No orders yet. Orders are created automatically when a customer approves a firm quote.</p>;
  }

  return (
    <div className="space-y-4" data-testid="admin-orders-section">
      {orders.map((o) => (
        <div key={o.id} className={`p-6 rounded-xl bg-card border ${o.order_number === focus ? "border-primary ring-2 ring-primary/40" : "border-border"}`} data-testid={`admin-order-card-${o.id}`} data-focused={o.order_number === focus || undefined}>
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex flex-wrap items-center gap-3">
              <span className="font-mono-tech text-sm font-bold text-primary">{o.order_number}</span>
              <h4 className="font-semibold font-heading">{o.title}</h4>
              <OrderStatusBadge status={o.status} />
            </div>
            <span className="text-xs text-muted-foreground">{fmtDate(o.created_at)}</span>
          </div>
          <p className="mt-1 text-sm text-muted-foreground" data-testid={`admin-order-customer-${o.id}`}>
            {o.customer?.name || "—"} · {o.customer?.email}{o.customer?.company ? ` · ${o.customer.company}` : ""} · from {o.rfq_number}{o.po_number ? ` · PO ${o.po_number}` : ""}
          </p>
          <div className="mt-4"><StageTrack status={o.status} heldFrom={o.held_from} /></div>
          <div className="mt-4 flex flex-wrap items-center gap-x-6 gap-y-2 text-sm" data-testid={`admin-order-commercial-${o.id}`}>
            <span className="font-mono-tech text-lg font-bold">{o.commercial?.price != null ? fmtINR(o.commercial.price) : "—"}</span>
            <span className="text-muted-foreground">Lead: <b>{o.lead_time_days ?? "—"}d</b></span>
            <span className="text-xs text-muted-foreground">Engineering source: {o.engineering?.source}{o.engineering?.understanding_revision != null ? ` (rev ${o.engineering.understanding_revision})` : ""}</span>
            {o.engineering?.costing_snapshot?.available && <span className="text-xs text-muted-foreground">Library estimate {fmtINR(o.engineering.costing_snapshot.estimate_min)}–{fmtINR(o.engineering.costing_snapshot.estimate_max)}</span>}
          </div>
          {o.assignment && (
            <div className="mt-3 text-sm" data-testid={`admin-order-assignment-${o.id}`}>
              Partner: <b>{o.assignment.partner_company}</b> ({o.assignment.partner_email}) · <Badge variant="outline">{o.assignment.status}</Badge>
              {o.assignment.note ? ` · ${o.assignment.note}` : ""}
            </div>
          )}
          {o.assignment_history?.length > 0 && (
            <div className="mt-2 text-xs text-muted-foreground space-y-0.5" data-testid={`admin-order-assignment-history-${o.id}`}>
              {o.assignment_history.map((h, i) => <p key={i}>Declined by {h.partner_company} · {fmtDate(h.responded_at)} · {h.decline_reason}</p>)}
            </div>
          )}
          {o.status === "ON_HOLD" && <p className="mt-3 text-sm text-amber-600 dark:text-amber-400">On hold (from {o.held_from}){o.hold_reason ? `: ${o.hold_reason}` : ""}</p>}
          {o.status === "CANCELLED" && <p className="mt-3 text-sm text-destructive">Cancelled{o.cancel_reason ? `: ${o.cancel_reason}` : ""}</p>}
          <OrderParts order={o} onDownload={onDownloadFile} downloading={downloading} testPrefix="admin-order" />
          <ProgressList order={o} onDownload={onDownloadFile} downloading={downloading} testPrefix="admin-order" />
          <QualitySummary order={o} viewer="admin" onDownload={onDownloadFile} downloading={downloading} />
          <DeliverySummary order={o} viewer="admin" onDownload={onDownloadFile} downloading={downloading} />
          <OrderDocuments order={o} role="admin" />

          {!["DELIVERY_CONFIRMED", "CANCELLED"].includes(o.status) && (
            <div className="mt-4 space-y-3">
              <Input value={note(o)} onChange={(e) => setNotes({ ...notes, [o.id]: e.target.value })} placeholder="Note / reason (required for cancellation)" className="max-w-md" data-testid={`admin-order-note-${o.id}`} />
              <div className="flex flex-wrap gap-3">
                {o.status === "ORDER_CREATED" && (
                  <Button size="sm" disabled={acting === o.id} onClick={() => confirm(o)} className="rounded-full" data-testid={`admin-order-confirm-btn-${o.id}`}>
                    <CheckCircle2 className="h-4 w-4 mr-1.5" strokeWidth={1.5} /> Confirm order
                  </Button>
                )}
                {o.status === "CONFIRMED" && <Candidates order={o} onAssigned={upsert} />}
                {QUALITY_STATES.includes(o.status) && <QualityPanel key={`${o.id}-${o.status}`} order={o} onUpdated={upsert} />}
                {(o.status === "QUALITY_PASSED" || DELIVERY_STATES.includes(o.status)) && <DispatchPanel key={`${o.id}-${o.status}-d`} order={o} onUpdated={upsert} />}
                {!["ON_HOLD", "DELIVERED"].includes(o.status) && (
                  <Button size="sm" variant="outline" disabled={acting === o.id} onClick={() => setStatus(o, "ON_HOLD")} className="rounded-full" data-testid={`admin-order-hold-btn-${o.id}`}>
                    <PauseCircle className="h-4 w-4 mr-1.5" strokeWidth={1.5} /> Hold
                  </Button>
                )}
                {o.status === "ON_HOLD" && (
                  <Button size="sm" variant="outline" disabled={acting === o.id} onClick={() => setStatus(o, "RESUME")} className="rounded-full" data-testid={`admin-order-resume-btn-${o.id}`}>
                    <PlayCircle className="h-4 w-4 mr-1.5" strokeWidth={1.5} /> Resume
                  </Button>
                )}
                {o.status !== "DELIVERED" && (
                  <Button size="sm" variant="ghost" disabled={acting === o.id} onClick={() => setStatus(o, "CANCELLED")} className="rounded-full text-muted-foreground" data-testid={`admin-order-cancel-btn-${o.id}`}>
                    <XOctagon className="h-4 w-4 mr-1.5" strokeWidth={1.5} /> Cancel
                  </Button>
                )}
              </div>
            </div>
          )}
          <OrderTimeline order={o} />
        </div>
      ))}
    </div>
  );
};
